import { useEffect } from "react";
import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import PageShell from "../components/PageShell";
import {
  LEGAL_EFFECTIVE_DATE,
  LEGAL_LOCALE,
  LEGAL_VERSION,
  legalMessages,
} from "../content/legalMessages";

const dateFormatter = new Intl.DateTimeFormat(LEGAL_LOCALE, {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

export default function LegalPage({ documentType }) {
  const document = legalMessages[documentType];
  const formattedDate = dateFormatter.format(new Date(`${LEGAL_EFFECTIVE_DATE}T00:00:00Z`));

  useEffect(() => {
    const previousTitle = window.document.title;
    window.document.title = `${document.title} | ARL`;
    return () => {
      window.document.title = previousTitle;
    };
  }, [document.title]);

  return (
    <PageShell
      title={document.title}
      contentSx={{ p: { xs: 2, sm: 3, md: 5 } }}
    >
      <Box sx={{ maxWidth: 840, mx: "auto" }}>
        <Stack spacing={2.5} sx={{ mb: { xs: 4, md: 5 } }}>
          <Typography variant="overline" color="primary.main">
            {document.eyebrow}
          </Typography>
          <Typography variant="h5" component="p" sx={{ maxWidth: 720, lineHeight: 1.45 }}>
            {document.summary}
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Chip
              label={`${document.effectiveLabel}: ${formattedDate}`}
              variant="outlined"
              size="small"
            />
            <Chip
              label={`${document.versionLabel}: ${LEGAL_VERSION}`}
              variant="outlined"
              size="small"
            />
          </Stack>
        </Stack>

        <Stack spacing={2.5} component="article">
          {document.sections.map((section, index) => (
            <Paper
              component="section"
              variant="outlined"
              key={section.title}
              aria-labelledby={`${documentType}-section-${index}`}
              sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 2 }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                <GavelOutlinedIcon color="primary" fontSize="small" aria-hidden="true" />
                <Typography
                  id={`${documentType}-section-${index}`}
                  variant="h6"
                  component="h2"
                >
                  {section.title}
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1.5}>
                {section.paragraphs?.map((paragraph) => (
                  <Typography key={paragraph} color="text.secondary">
                    {paragraph}
                  </Typography>
                ))}
                {section.list && (
                  <Stack component="ul" spacing={0.75} sx={{ m: 0, ps: 2.5 }}>
                    {section.list.map((item) => (
                      <Typography component="li" variant="body2" color="text.secondary" key={item}>
                        {item}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Box>
    </PageShell>
  );
}
